import {
  Row,
  Col,
  Button,
  Card,
  CardBody,
  CardHeader,
  Table,
  Badge,
  Form,
} from "react-bootstrap";
import { useState, useEffect, useRef } from "react";
import crudApi from "@serie3/common/api/crudApi";
import GroupConfig from "@serie3/common/Modules/Groups/Resources/Endpoints";
import { useFormik } from "formik";
import GroupForm from "@serie3/common/Modules/Groups/Forms/GroupForm";
import TextField from "../../../../../Form/TextField";
import { useTranslation } from "react-i18next";
import SaveButton from "../../../../../shared/Buttons/SaveButton";
import AddButton from "../../../../../shared/Buttons/AddButton";
import InviteModal from "./InviteModal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrash, faImage } from "@fortawesome/free-solid-svg-icons";
import GroupLogo, { invalidateGroupLogo, useGroupLogo } from "../../../../../shared/GroupLogo";
import { withdrawInvitation } from "@serie3/common/Modules/Invitations/Domain/Functions";
import DeleteModal from "./DeleteModal";
import { LeaveGroup, RemoveMember } from "@serie3/common/Modules/Groups/Domain/Functions";
import { Roles, normalizeCaps, hasAny, has, getRoleRights} from "@serie3/common/config/RoleRights";
import MediaplannerGroupEndpoints from "@serie3/mymediaplanner/Modules/Groups/endpoints";

const MAX_LOGO_BYTES = 5 * 1024 * 1024;
const LOGO_TYPES = ["image/jpeg", "image/png", "image/gif", "image/webp"];

/**
 * Upload / replace / remove the group's logo. Admins only (the endpoint
 * enforces it too). Shown in the group header for every member.
 */
const GroupLogoCard = ({ group, onChanged }) => {
  const { t } = useTranslation();
  const [version, setVersion] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);
  const url = useGroupLogo({ groupId: group?.id, version });

  const refresh = () => {
    invalidateGroupLogo(group.id);
    setVersion((v) => v + 1);
    onChanged?.();
  };

  const onFileChosen = (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    setError(null);

    if (!LOGO_TYPES.includes(file.type)) {
      setError(t("Backend.Groups.Logo.TypeError", "Please choose a JPEG, PNG, GIF or WebP image."));
      return;
    }
    if (file.size > MAX_LOGO_BYTES) {
      setError(t("Backend.Groups.Logo.SizeError", "The image must be 5 MB or smaller."));
      return;
    }

    const reader = new FileReader();
    reader.onloadend = async () => {
      setBusy(true);
      const response = await crudApi.edit(MediaplannerGroupEndpoints.endpoints.logo + group.id, {
        file_string: reader.result,
        file_name: file.name,
      });
      setBusy(false);
      if (response.ok) {
        refresh();
      } else {
        setError(t("Backend.Groups.Logo.UploadError", "The logo could not be saved."));
      }
    };
    reader.readAsDataURL(file);
  };

  const removeLogo = async () => {
    setBusy(true);
    setError(null);
    const response = await crudApi.del(MediaplannerGroupEndpoints.endpoints.logo, group.id);
    setBusy(false);
    if (response.ok) refresh();
  };

  return (
    <Card className="mb-4">
      <CardHeader>
        <h5 className="card-title">{t("Backend.Groups.Logo.Title", "Group logo")}</h5>
      </CardHeader>
      <CardBody>
        <p className="text-muted small">
          {t("Backend.Groups.Logo.Hint", "Shown next to the group name for every member.")}
        </p>
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <GroupLogo
            groupId={group?.id}
            version={version}
            size={72}
            style={{ border: "2px solid #e5e7eb", borderRadius: 12, background: "#fff" }}
            fallback={<FontAwesomeIcon icon={faImage} style={{ color: "#c5cad3", fontSize: 28 }} />}
          />
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <input
              ref={inputRef}
              type="file"
              accept={LOGO_TYPES.join(",")}
              onChange={onFileChosen}
              style={{ display: "none" }}
            />
            <Button
              variant="outline-primary"
              size="sm"
              disabled={busy}
              onClick={() => inputRef.current?.click()}
              style={{ borderRadius: 8, fontSize: 13 }}
            >
              <FontAwesomeIcon icon={faImage} style={{ marginRight: 6 }} />
              {url
                ? t("Backend.Groups.Logo.Change", "Change logo")
                : t("Backend.Groups.Logo.Upload", "Upload logo")}
            </Button>
            {url && (
              <Button
                variant="outline-secondary"
                size="sm"
                disabled={busy}
                onClick={removeLogo}
                style={{ borderRadius: 8, fontSize: 13 }}
              >
                <FontAwesomeIcon icon={faTrash} style={{ marginRight: 6 }} />
                {t("Backend.Groups.Logo.Remove", "Remove")}
              </Button>
            )}
          </div>
        </div>
        {error && <div style={{ fontSize: 12, color: "#dc2626", marginTop: 10 }}>{error}</div>}
      </CardBody>
    </Card>
  );
};

const Settings = ({ groups, setGroups, group, rights, setGroupName, onLogoChanged }) => {
  const [invitations, setInvitations] = useState([]);
  const [members, setMembers] = useState([]);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [archived, setArchived] = useState(!!group?.archived);
  const [quicklink, setQuicklink] = useState(!!group?.quicklink);
  const [overlayCss, setOverlayCss] = useState("");
  const [builtInCss, setBuiltInCss] = useState("");
  const [savingOverlayCss, setSavingOverlayCss] = useState(false);

  const { t } = useTranslation();

  const groupForm = GroupForm.createGroupForm(groups, setGroups, setGroupName);
  const formik = useFormik(groupForm);

  const caps = normalizeCaps(rights);
  const RoleRights = getRoleRights(caps);
  const isOwner = has(caps, Roles.OWNER);

  const isAdmin = hasAny(caps, [Roles.OWNER, Roles.ADMIN]);

  /** Default title CSS for images in posts shared into this group. */
  useEffect(() => {
    if (!group?.id) return undefined;
    let cancelled = false;
    crudApi
      .justGet(MediaplannerGroupEndpoints.endpoints.overlay + group.id)
      .then((response) => {
        if (cancelled || !response?.ok) return;
        setOverlayCss(response.data?.css || "");
        setBuiltInCss(response.data?.builtInCss || "");
      });
    return () => {
      cancelled = true;
    };
  }, [group?.id]);

  const saveOverlayCss = async () => {
    setSavingOverlayCss(true);
    await crudApi.edit(MediaplannerGroupEndpoints.endpoints.overlay + group.id, {
      css: overlayCss.trim() === "" ? null : overlayCss,
    });
    setSavingOverlayCss(false);
  };

  const groupPayload = () => {
    const { system_group, ...rest } = group;
    return rest;
  };

  const toggleArchived = async () => {
    const updated = { ...groupPayload(), archived: !archived };
    const response = await crudApi.edit(GroupConfig.endpoints.edit, updated);
    if (response.status === 200) setArchived(!archived);
  };

  const toggleQuicklink = async () => {
    const updated = { ...groupPayload(), quicklink: !quicklink };
    const response = await crudApi.edit(GroupConfig.endpoints.edit, updated);
    if (response.status === 200) setQuicklink(!quicklink);
  };

  const removeAdmin = async (member) => {
    const response = await crudApi.del(
      MediaplannerGroupEndpoints.endpoints.removeAdmin + group.id + "/",
      member.id,
    );
    if (response.status === 200) {
      setMembers((prev) => prev.filter((m) => m.id !== member.id));
    }
  };

  useEffect(() => {
    // Only load if we have a valid group AND user is owner/admin
    if (!group?.id || !hasAny(caps, [Roles.OWNER, Roles.ADMIN])) return;

    const getInvitations = async () => {
      try {
        const url = `${GroupConfig.endpoints.listInvitations}${group.id}`;
        const response = await crudApi.justGet(url);
        if (response?.ok) {
          const list = Array.isArray(response.data)
            ? response.data
            : response.data?.items ?? [];
          setInvitations(list);
        } else {
          setInvitations([]);
        }
      } catch (e) {
        console.error("Failed to load invitations", e);
        setInvitations([]);
      }
    };

    const getMembers = async () => {
      try {
        const url = `${GroupConfig.endpoints.showMembers}${group.id}`;
        const response = await crudApi.justGet(url);
        if (response?.ok) {
          const list = Array.isArray(response.data)
            ? response.data
            : response.data?.items ?? [];
          setMembers(list);
        } else {
          setMembers([]);
        }
      } catch (e) {
        console.error("Failed to load members", e);
        setMembers([]);
      }
    };

    getInvitations();
    getMembers();
    formik.setValues(GroupForm.setEditData(group));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [group]);

  return (
    <>
      <InviteModal
        show={showInviteModal}
        setShow={setShowInviteModal}
        dataList={invitations}
        setDataList={setInvitations}
        groupId={group?.id}
      />
      <DeleteModal
        group={group}
        show={showDeleteModal}
        setShow={setShowDeleteModal}
      />
      <Row>
        <Col>
          {RoleRights.edit && (
                <Card>
                  <CardHeader>
                    <h5 className="card-title">Edit Group Name</h5>
                  </CardHeader>
                  <CardBody>
                    <Row>
                      <Col>
                        <TextField
                          formik={formik}
                          valueName={"name"}
                          style={{ marginBottom: "10px" }}
                        />
                      </Col>
                      <Col>
                        <SaveButton
                          submitFunction={(e) => {
                            formik.handleSubmit(e);
                            e.preventDefault();
                          }}
                        />
                      </Col>
                    </Row>
                  </CardBody>
                </Card>
              )}

              {RoleRights.invite && (
                <Card className="mb-4">
                  <Card.Header>
                    <div className="page-title-box d-flex align-items-center justify-content-between">
                      <h5 className="card-title mb-0">Invitations</h5>
                      <AddButton
                        addFunction={() => {
                          setShowInviteModal(true);
                        }}
                      />
                    </div>
                  </Card.Header>
                  <Card.Body>
                    <div className="table-responsive">
                      <Table hover size="sm">
                        <thead>
                          <tr>
                            <th>ID</th>
                            <th>Email</th>
                            <th>Invited at</th>
                            <th>Promo code</th>
                            <th>Roles</th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {(invitations ?? []).map((inv, index) => (
                            <tr key={inv?.id ?? Math.random()}>
                              <td>{inv?.id ?? "—"}</td>
                              <td>{inv?.user_mail ?? "—"}</td>
                              <td>{inv?.invited_at ?? "—"}</td>
                              <td>{inv?.promo_code ?? "—"}</td>
                              <td>
                                {(inv?.caps ?? []).map((cap) => (
                                  <Badge
                                    bg="secondary"
                                    className="me-1"
                                    key={cap}
                                  >
                                    {cap}
                                  </Badge>
                                ))}
                              </td>
                              <td>
                                <FontAwesomeIcon
                                  style={{ color: "red" }}
                                  icon={faTrash}
                                  onClick={() => {
                                    withdrawInvitation(
                                      invitations,
                                      setInvitations,
                                      inv,
                                      group?.id,
                                      index
                                    );
                                  }}
                                />
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  </Card.Body>
                </Card>
              )}

              {RoleRights.manageRoles && (
                <Card className="mb-4">
                  <Card.Header>
                    <h5 className="card-title mb-0">Manage Members</h5>
                  </Card.Header>
                  <Card.Body>
                    <div className="table-responsive">
                      <Table hover size="sm">
                        <thead>
                          <tr>
                            <th>ID</th>
                            <th>Email</th>
                            <th>Roles</th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {(members ?? []).map((member, index) => (
                            <tr key={member?.id ?? Math.random()}>
                              <td>{member?.id ?? "—"}</td>
                              <td>{member?.user_info.email ?? "—"}</td>
                              <td>
                                {(caps ?? []).map((cap) => (
                                  <Badge
                                    bg="secondary"
                                    className="me-1"
                                    key={cap}
                                  >
                                    {member.caps[0]}
                                  </Badge>
                                ))}
                              </td>
                              <td>
                                {isOwner && member.caps?.includes(Roles.ADMIN) && (
                                  <FontAwesomeIcon
                                    style={{ color: "red", cursor: "pointer" }}
                                    icon={faTrash}
                                    onClick={() => removeAdmin(member)}
                                  />
                                )}
                                {!member.caps?.includes(Roles.ADMIN) && (
                                  <FontAwesomeIcon
                                    style={{ color: "red", cursor: "pointer" }}
                                    icon={faTrash}
                                    onClick={() => {
                                      RemoveMember(
                                        members,
                                        setMembers,
                                        member,
                                        group?.id,
                                        index
                                      );
                                    }}
                                  />
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  </Card.Body>
                </Card>
              )}

              {isAdmin && group?.id && !group?.system_group && (
                <GroupLogoCard group={group} onChanged={onLogoChanged} />
              )}

              {isAdmin && (
                <Card className="mb-4">
                  <CardHeader>
                    <h5 className="card-title">Group Actions</h5>
                  </CardHeader>
                  <CardBody className="d-flex gap-2">
                    <Button
                      variant={archived ? "secondary" : "warning"}
                      onClick={toggleArchived}
                    >
                      {archived ? "Unarchive" : "Archive"}
                    </Button>
                    <Button
                      variant={quicklink ? "primary" : "outline-primary"}
                      onClick={toggleQuicklink}
                    >
                      {quicklink ? "Remove from Quicklink" : "Add to Quicklink"}
                    </Button>
                  </CardBody>
                </Card>
              )}

              {isAdmin && (
                <Card className="mb-4">
                  <CardHeader>
                    <h5 className="card-title">
                      {t("Backend.Groups.OverlayCss.Title", "Default title CSS")}
                    </h5>
                  </CardHeader>
                  <CardBody>
                    <p className="text-muted small">
                      {t(
                        "Backend.Groups.OverlayCss.Hint",
                        "Used for the title on every image in posts shared into this group, unless an image has its own CSS. Leave empty to use each post author's personal default."
                      )}
                    </p>
                    <Form.Control
                      as="textarea"
                      rows={10}
                      spellCheck={false}
                      value={overlayCss}
                      placeholder={builtInCss}
                      onChange={(event) => setOverlayCss(event.target.value)}
                      style={{ fontFamily: "monospace", fontSize: "0.8rem" }}
                    />
                    <div className="d-flex gap-2 mt-2">
                      <Button onClick={saveOverlayCss} disabled={savingOverlayCss}>
                        {t("Backend.Groups.OverlayCss.Save", "Save default CSS")}
                      </Button>
                      {overlayCss.trim() !== "" && (
                        <Button variant="link" onClick={() => setOverlayCss("")}>
                          {t("Backend.Groups.OverlayCss.Clear", "Clear (use each author's default)")}
                        </Button>
                      )}
                    </div>
                  </CardBody>
                </Card>
              )}

              {RoleRights.delete && (
                <Card>
                  <CardHeader>
                    <h5 className="card-title">Delete Group</h5>
                  </CardHeader>
                  <CardBody>
                    <Button
                      variant="danger"
                      onClick={() => setShowDeleteModal(true)}
                    >
                      Delete Group
                    </Button>
                  </CardBody>
                </Card>
              )}

          {RoleRights.leave && (
            <Card>
              <CardHeader>
                <h5 className="card-title">Leave Roles</h5>
              </CardHeader>
              <CardBody>
                <Button
                  variant="outline-danger"
                  onClick={() => {
                    LeaveGroup(group?.id);
                  }}
                >
                  Leave Group
                </Button>
              </CardBody>
            </Card>
          )}
        </Col>
      </Row>
    </>
  );
};

export default Settings;
